import React, { useState, useEffect } from 'react';
import { Search, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import { casePictureService, type PatientCasePicture } from '../../lib/services.patientinfo';
import { patientService } from '../../lib/services';
import toast from 'react-hot-toast';

const CasePicture: React.FC = () => {
  const [pictures, setPictures] = useState<PatientCasePicture[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [patients, setPatients] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    loadPictures();
    loadPatients();
  }, []);

  const loadPictures = async () => {
    try {
      const data = await casePictureService.getAll();
      setPictures(data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load case pictures');
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async () => {
    try {
      const data = await patientService.getAll();
      setPatients(data);
    } catch (error: any) {
      console.error('Failed to load patients:', error);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedPatient) {
      toast.error('Please select a patient and file');
      return;
    }

    setUploading(true);
    try {
      const pictureUrl = await casePictureService.uploadPicture(selectedFile, selectedPatient);
      
      await casePictureService.create({
        patient_id: selectedPatient,
        picture_url: pictureUrl,
        picture_type: 'case',
        upload_date: new Date().toISOString().split('T')[0],
        notes: notes || undefined,
        uploaded_by: 'Current User'
      });

      toast.success('Case picture uploaded successfully');
      setSelectedFile(null);
      setSelectedPatient('');
      setNotes('');
      loadPictures();
    } catch (error: any) {
      toast.error(error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this case picture?')) return;
    try {
      await casePictureService.delete(id);
      toast.success('Case picture deleted successfully');
      loadPictures();
    } catch (error: any) {
      toast.error(error.message || 'Delete failed');
    }
  };

  const filteredPictures = pictures.filter(picture => {
    const patient = patients.find(p => p.id === picture.patient_id);
    const searchLower = search.toLowerCase();
    return (
      patient?.name.toLowerCase().includes(searchLower) ||
      patient?.unique_id?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Case Picture</h1>
          <p className="text-gray-600">Upload and manage patient case pictures.</p>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Case Picture</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Patient *</label>
            <select
              value={selectedPatient}
              onChange={(e) => setSelectedPatient(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">Choose Patient</option>
              {patients.map(patient => (
                <option key={patient.id} value={patient.id}>
                  {patient.name} ({patient.unique_id})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Picture *</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add any notes about this case picture..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          <div className="md:col-span-2">
            <button
              onClick={handleUpload}
              disabled={uploading || !selectedFile || !selectedPatient}
              className="flex items-center space-x-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              <span>{uploading ? 'Uploading...' : 'Upload Picture'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by patient name or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
        </div>
      </div>

      {/* Pictures Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPictures.map((picture) => {
          const patient = patients.find(p => p.id === picture.patient_id);
          return (
            <div key={picture.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="aspect-w-16 aspect-h-12 bg-gray-100">
                {picture.picture_url ? (
                  <img
                    src={picture.picture_url}
                    alt="Case picture"
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-48">
                    <ImageIcon className="w-12 h-12 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{patient?.name || 'Unknown'}</h3>
                    <p className="text-xs text-gray-500">{patient?.unique_id || '-'}</p>
                  </div>
                  <button
                    onClick={() => handleDelete(picture.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-600 mb-2">
                  {new Date(picture.upload_date).toLocaleDateString()}
                </p>
                {picture.notes && (
                  <p className="text-xs text-gray-700 line-clamp-2">{picture.notes}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredPictures.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No case pictures found</p>
        </div>
      )}
    </div>
  );
};

export default CasePicture;
